/*
 * Copyright (c) 2024, King Living
 * Store Locator Page - Showrooms Listing
 * STORLOC-01: Store listing page with new layout
 *
 * Layout: Search section (1/3) | Map (2/3)
 *         Store list below search section
 */

import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {useLocation} from 'react-router-dom';

import {
  Alert,
  AlertIcon,
  Box,
  Flex,
  Grid,
  GridItem,
  Heading,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Spinner,
  Text,
  useMultiStyleConfig,
  VStack,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {SearchIcon} from '@salesforce/retail-react-app/app/components/icons';

import {useAccessToken, useSearchStores} from '@salesforce/commerce-sdk-react';
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation';
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin';

import {useGlobalConfigBySiteId} from '@amblique/king-living/app/contentful/hooks';

import Seo from '@amblique/king-living/app/components/seo';
import {buildPageJsonLdGraph} from '@amblique/king-living/app/utils/seo-utils';

import AddressAutocompleteUI from '@amblique/king-living/app/components/address-autocomplete/address-autocomplete-ui';
import {useAddressAutocomplete} from '@amblique/king-living/app/hooks/use-address-autocomplete';
import {
  searchAdminStores,
  useMultiSiteStores,
} from '@amblique/king-living/app/hooks/use-multi-site-stores';
import {useGeocoding} from '@amblique/king-living/app/hooks/use-geocoding';
import {
  buildShowroomCountryTabs,
  getAdminFetchCountryCodes,
  normalizeShowroomCountryCode,
  normalizeShowroomCountryCodes,
  replaceShowroomCountryInUrl,
  resolveCurrentSiteStoreTotal,
  resolveShowroomCountryFromSearchParams,
  toGeocodeCountryComponent,
} from '@amblique/king-living/app/utils/showroom-countries';

import GoogleMap from '../../components/google-map';
import {ShowroomListHeader, StoreTile} from '../../components/showroom';
import {
  DEFAULT_STORE_SEARCH_RADIUS,
  MAX_STORE_SEARCH_RADIUS,
  SHOWROOM_COUNTRY_TO_SITE_ID,
  getSiteConfig,
} from '../../constants/site-config';
import {
  computeStoreClusterFocusBounds,
  createStoreMarkers,
  useGoogleMapsIntegration,
  useMarkerIconSize,
} from '../../hooks/use-google-maps-integration';
import {
  getMyShowroomFromCookie,
  getStoreSlug,
  setMyShowroomToCookie,
  SHOWROOM_CHANGED_EVENT,
} from '../../utils/utils';

// Location permission states
const LOCATION_PERMISSION = {
  UNKNOWN: 'unknown', // Haven't asked yet
  GRANTED: 'granted', // User allowed
  DENIED: 'denied', // User denied
  UNAVAILABLE: 'unavailable', // Geolocation not supported
  TIMEOUT: 'timeout', // Request timed out
  POSITION_ERROR: 'position_error', // Could not determine position
};

// KL-991 — Google Places autocomplete on the showroom search box.
// Suggestions fire only after this many characters and this debounce, to keep Places
// Autocomplete API calls (and their cost) down while still surfacing suggestions as the
// shopper types a suburb / postcode / address.
const AUTOCOMPLETE_MIN_CHARS = 3;
const AUTOCOMPLETE_DEBOUNCE_MS = 500;

// Location/GPS Icon
const LocationIcon = (props) => (
  <Icon viewBox="0 0 24 24" {...props}>
    <path
      d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm8.94 3c-.46-4.17-3.77-7.48-7.94-7.94V1h-2v2.06C6.83 3.52 3.52 6.83 3.06 11H1v2h2.06c.46 4.17 3.77 7.48 7.94 7.94V23h2v-2.06c4.17-.46 7.48-3.77 7.94-7.94H23v-2h-2.06zM12 19c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z"
      fill="currentColor"
    />
  </Icon>
);

/**
 * Store Locator Page Component
 *
 * Layout: Search section (1/3 left) | Map (2/3 right)
 *         Store list below search section
 */
const StoreLocatorPage = () => {
  const intl = useIntl();
  const location = useLocation();
  const navigate = useNavigation();
  const appOrigin = useAppOrigin();
  const searchDropdownRef = useRef(null);
  const storeListingColumnRef = useRef(null);
  const showroomsGridRef = useRef(null);
  const shouldScrollNewMyShowroomIntoViewRef = useRef(false);
  const countryUrlHydratedRef = useRef(false);
  const initialStoreSearchStartedRef = useRef(false);
  const styles = useMultiStyleConfig('Showrooms', {});

  // Get site information
  const {site, buildUrl} = useMultiSite();
  const {getTokenWhenReady} = useAccessToken();
  const siteConfig = getSiteConfig(site?.id);
  const defaultLocation = siteConfig.defaultLocation;
  const currentCountryCode = normalizeShowroomCountryCode(siteConfig.countryCode || 'AU');

  // Fetch GlobalConfig from Contentful by siteId
  const {
    data: globalConfig,
    isLoading: isLoadingGlobalConfig,
  } = useGlobalConfigBySiteId(site?.id, {
    variant: 'full', // Get full nested data including showroomHero
  });

  // Site ID for cookie operations
  const siteId = site?.id;

  const cmsCountryCodes = useMemo(
    () => normalizeShowroomCountryCodes(globalConfig?.countries),
    [globalConfig?.countries]
  );

  const adminFetchCountries = useMemo(
    () => getAdminFetchCountryCodes(cmsCountryCodes, siteId),
    [cmsCountryCodes, siteId]
  );

  const {
    byCountry: adminByCountry,
    isLoading: isAdminStoresLoading,
  } = useMultiSiteStores(adminFetchCountries, {enabled: !isLoadingGlobalConfig && adminFetchCountries.length > 0});
  const adminHydrated = adminFetchCountries.length === 0 || !isAdminStoresLoading;

  // Non-AU current badge: Shopper Search total (AU uses Admin AU split — skip this call).
  const {data: currentSiteCatalog} = useSearchStores(
    {
      parameters: {
        latitude: defaultLocation?.lat,
        longitude: defaultLocation?.lng,
        maxDistance: MAX_STORE_SEARCH_RADIUS,
        distanceUnit: siteConfig.distanceUnit,
        limit: 1,
        locale: siteConfig.defaultLocale,
      },
    },
    {
      enabled:
        currentCountryCode !== 'AU' &&
        defaultLocation?.lat != null &&
        defaultLocation?.lng != null,
    }
  );
  const currentSiteStoreTotal = useMemo(
    () => resolveCurrentSiteStoreTotal({
      currentCountryCode,
      shopperTotal: currentSiteCatalog?.total,
      adminByCountry,
    }),
    [adminByCountry, currentCountryCode, currentSiteCatalog?.total]
  );

  const [selectedCountryCode, setSelectedCountryCode] = useState(currentCountryCode);
  const [adminSearchStores, setAdminSearchStores] = useState(null);
  const [adminSearchOrigin, setAdminSearchOrigin] = useState(null);
  const [adminSearchError, setAdminSearchError] = useState(null);
  const [isAdminSearchLoading, setIsAdminSearchLoading] = useState(false);
  const {geocodeAddress, extractCoordinates, lookupPlaceById} = useGeocoding();
  const markerIconSize = useMarkerIconSize();

  const isViewingCurrentSite = selectedCountryCode === currentCountryCode;
  // Autocomplete/geocode bias follows selected country tab (SA).
  const geocodeCountryComponent = toGeocodeCountryComponent(selectedCountryCode);

  const jsonLdGraph = useMemo(
    () => buildPageJsonLdGraph({
      appOrigin,
      pathname: location.pathname,
      siteId,
    }),
    [appOrigin, location.pathname, siteId]
  );

  const showroomSeoTitle = intl.formatMessage({
    defaultMessage: 'Find a showroom',
    id: 'store_locator.heading.title',
  });
  const showroomSeoDescription = intl.formatMessage({
    defaultMessage: 'Visit a King showroom',
    id: 'showroom_list_header.fallback_title',
  });

  // Get site preferences
  const apiKey = site.preferences?.GOOGLE_MAPS_API_KEY;
  const defaultZoom = 10;

  // km — address/postcode and "Use my location" (initial GPS load uses max radius in the hook)
  const rawRadiusPreference = site.preferences?.STORE_SEARCH_RADIUS;
  const parsedPreference =
    rawRadiusPreference !== undefined &&
    rawRadiusPreference !== null &&
    String(rawRadiusPreference).trim() !== '' ?
      parseInt(String(rawRadiusPreference), 10) :
      NaN;
  const searchRadius =
    Number.isFinite(parsedPreference) && parsedPreference > 0 ?
      parsedPreference :
      siteConfig.searchRadius ?? DEFAULT_STORE_SEARCH_RADIUS;

  const countryPlacesScope = useMemo(() => {
    const {defaultLocation, defaultLocale, distanceUnit} = getSiteConfig(
      SHOWROOM_COUNTRY_TO_SITE_ID[selectedCountryCode] || siteId
    );
    const language = defaultLocale?.split('-')[0];
    const autocomplete = {};
    const geocode = {};

    if (geocodeCountryComponent) {
      autocomplete.components = `country:${geocodeCountryComponent}`;
      geocode.components = `country:${geocodeCountryComponent}`;
      geocode.region = geocodeCountryComponent;
    }

    if (defaultLocation?.lat != null && defaultLocation?.lng != null) {
      autocomplete.location = `${defaultLocation.lat},${defaultLocation.lng}`;
      autocomplete.radius = Math.min(Math.round(searchRadius * 1000), 50000);
    }

    if (language) {
      autocomplete.language = language;
      geocode.language = language;
    }

    return {
      autocomplete,
      distanceUnit,
      geocode,
      geocodePlaceId: language ? {language} : {},
    };
  }, [geocodeCountryComponent, searchRadius, selectedCountryCode, siteId]);

  const resetAdminSearch = useCallback(() => {
    setAdminSearchStores(null);
    setAdminSearchOrigin(null);
    setAdminSearchError(null);
  }, []);

  // Hook reads this after first GPS search with 0 results to run one default-site search (limit 20).
  const initialSearchUsedUserCoordsRef = useRef(false);

  // Custom hooks for Google Maps integration
  const {
    stores,
    mapCenter,
    mapBounds,
    markers,
    isSearchLoading,
    searchError,
    isFallbackResults,
    searchQuery,
    isGeolocationLoading,
    getCurrentPosition,
    handleUseMyLocation,
    handleLocationSearch,
    handlePlaceSelect,
    searchByCoordinates,
  } = useGoogleMapsIntegration({
    countryCode: siteConfig.countryCode,
    locale: siteConfig.defaultLocale,
    distanceUnit: siteConfig.distanceUnit,
    searchRadius,
    defaultLocation,
    initialSearchUsedUserCoordsRef,
  });

  // KL-991 — Google Places autocomplete (same server-proxied stack as checkout/PDP:
  // /server/autocomplete + GOOGLE_GEOLOCATION_API_KEY). Predictions are shown in the
  // existing search dropdown; selecting one resolves place_id → coordinates → store search.
  const {
    predictions,
    getSuggestions,
    clearSuggestions,
  } = useAddressAutocomplete();
  // True only while the shopper is typing — so programmatic value changes (selecting a
  // prediction, "Use my location", clearing) never re-trigger a suggestions request.
  const isUserTypingRef = useRef(false);

  // Local state
  const [searchInput, setSearchInput] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [myShowroom, setMyShowroom] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [locationPermission, setLocationPermission] = useState(LOCATION_PERMISSION.UNKNOWN);
  // KL-1081 — once the shopper selects a prediction (or searches / uses location / taps away),
  // suppress the predictions dropdown until they start typing again. This also defeats a race
  // where a getSuggestions request from the last keystroke resolves AFTER the selection and
  // repopulates the list, re-opening the dropdown.
  const [suppressPredictions, setSuppressPredictions] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchDropdownRef.current && !searchDropdownRef.current.contains(event.target)) {
        setIsSearchFocused(false);
        // KL-991 — also dismiss any open Places predictions when clicking away.
        isUserTypingRef.current = false;
        clearSuggestions();
        setSuppressPredictions(true);
      }
    };

    if (isSearchFocused) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSearchFocused, clearSuggestions]);

  // Load my showroom from cookie on mount and listen for changes
  useEffect(() => {
    if (!siteId) return;

    // Load initial value from cookie
    const savedShowroom = getMyShowroomFromCookie(siteId);

    if (savedShowroom) {
      setMyShowroom(savedShowroom);
    }

    // Listen for showroom changes from other components (e.g., ShowroomSelectionPopup)
    const handleShowroomChanged = (event) => {
      setMyShowroom(event.detail?.showroom || null);
    };

    window.addEventListener(SHOWROOM_CHANGED_EVENT, handleShowroomChanged);

    return () => {
      window.removeEventListener(SHOWROOM_CHANGED_EVENT, handleShowroomChanged);
    };
  }, [siteId]);

  // First paint: try GPS (max radius); on failure use site default. Hook handles 0-store GPS → default (limit 20).
  useEffect(() => {
    if (!isInitialLoad || !apiKey || !defaultLocation) return;
    if (initialStoreSearchStartedRef.current) return;

    initialStoreSearchStartedRef.current = true;

    const endInitialLoad = () => setIsInitialLoad(false);
    const runInitialSearch = (latitude, longitude, usedGps) => {
      initialSearchUsedUserCoordsRef.current = usedGps;

      return searchByCoordinates({latitude, longitude})
        .then(endInitialLoad)
        .catch((error) => {
          console.error('Failed to load stores on page load:', error);
          endInitialLoad();
        });
    };

    getCurrentPosition()
      .then((coords) => runInitialSearch(coords.latitude, coords.longitude, true))
      .catch(() => runInitialSearch(defaultLocation.lat, defaultLocation.lng, false));
  }, [isInitialLoad, apiKey, defaultLocation, searchByCoordinates, getCurrentPosition]);

  const setLocationPermissionFromError = useCallback((error) => {
    const errorMessage = error?.message || '';

    if (errorMessage.includes('denied')) {
      setLocationPermission(LOCATION_PERMISSION.DENIED);
    } else if (errorMessage.includes('timed out') || errorMessage.includes('timeout')) {
      setLocationPermission(LOCATION_PERMISSION.TIMEOUT);
    } else if (errorMessage.includes('not supported')) {
      setLocationPermission(LOCATION_PERMISSION.UNAVAILABLE);
    } else if (errorMessage.includes('unavailable') || errorMessage.includes('Location information')) {
      setLocationPermission(LOCATION_PERMISSION.POSITION_ERROR);
    } else {
      setLocationPermission(LOCATION_PERMISSION.POSITION_ERROR);
    }
  }, []);

  const dismissSearchSuggestions = useCallback(() => {
    isUserTypingRef.current = false;
    clearSuggestions();
    setSuppressPredictions(true);
    setIsSearchFocused(false);
  }, [clearSuggestions]);

  const runAdminStoreSearchAt = useCallback(async (latitude, longitude) => {
    setIsAdminSearchLoading(true);
    setAdminSearchError(null);
    setAdminSearchOrigin({lat: latitude, lng: longitude});

    try {
      const siteIdForSearch =
        SHOWROOM_COUNTRY_TO_SITE_ID[selectedCountryCode] || siteId;
      const result = await searchAdminStores(
        {
          latitude,
          longitude,
          maxDistance: searchRadius,
          siteId: siteIdForSearch,
          countryCode: selectedCountryCode,
        },
        getTokenWhenReady
      );

      setAdminSearchStores(Array.isArray(result?.data) ? result.data : []);
      setLocationPermission(LOCATION_PERMISSION.UNKNOWN);
    } catch (error) {
      console.error('Admin store search failed:', error);
      setAdminSearchStores([]);
      setAdminSearchOrigin(null);
      setAdminSearchError(
        'Something went wrong with the search. Please try again.'
      );
    } finally {
      setIsAdminSearchLoading(false);
    }
  }, [getTokenWhenReady, searchRadius, selectedCountryCode, siteId]);

  const performStoreLocatorSearch = useCallback(async ({
    query,
    placeId,
    label,
    useCurrentLocation = false,
  } = {}) => {
    dismissSearchSuggestions();

    if (useCurrentLocation) {
      setSearchInput('');
      if (!isViewingCurrentSite) {
        setAdminSearchError(null);
      }

      try {
        if (isViewingCurrentSite) {
          const result = await handleUseMyLocation();

          if (result?.usedFallback) {
            setLocationPermissionFromError(result.error);
          } else {
            setLocationPermission(LOCATION_PERMISSION.GRANTED);
          }

          return;
        }

        const coords = await getCurrentPosition();

        await runAdminStoreSearchAt(coords.latitude, coords.longitude);
        setLocationPermission(LOCATION_PERMISSION.GRANTED);
      } catch (error) {
        console.error('Location access failed:', error);
        setLocationPermissionFromError(error);
        if (!isViewingCurrentSite) {
          setAdminSearchStores([]);
        }
      }

      return;
    }

    const trimmed = (query || label || '').trim();

    if (!placeId && !trimmed) return;

    if (label || trimmed) {
      setSearchInput(label || trimmed);
    }

    try {
      if (isViewingCurrentSite) {
        if (placeId) {
          await handlePlaceSelect(placeId, label || trimmed);
        } else {
          await handleLocationSearch(trimmed);
        }

        setLocationPermission(LOCATION_PERMISSION.UNKNOWN);

        return;
      }

      setAdminSearchError(null);

      let coords = null;

      if (placeId) {
        coords = extractCoordinates(
          await lookupPlaceById(placeId, countryPlacesScope.geocodePlaceId)
        );
      } else {
        coords = extractCoordinates(
          await geocodeAddress(trimmed, countryPlacesScope.geocode)
        );
      }

      if (!coords?.lat || !coords?.lng) {
        setAdminSearchStores([]);
        setAdminSearchError(
          placeId ?
            `NO_RESULTS:${label || trimmed}` :
            `No results found for "${trimmed}". Please try a different search term.`
        );

        return;
      }

      await runAdminStoreSearchAt(coords.lat, coords.lng);
    } catch (error) {
      console.error('Search failed:', error);
      if (!isViewingCurrentSite) {
        setAdminSearchStores([]);
        if (error?.message?.startsWith('NO_RESULTS:')) {
          setAdminSearchError(error.message);
        } else {
          setAdminSearchError(
            'Something went wrong with the search. Please try again.'
          );
        }
      }
    }
  }, [
    countryPlacesScope,
    dismissSearchSuggestions,
    extractCoordinates,
    geocodeAddress,
    getCurrentPosition,
    handleLocationSearch,
    handlePlaceSelect,
    handleUseMyLocation,
    isViewingCurrentSite,
    lookupPlaceById,
    runAdminStoreSearchAt,
    setLocationPermissionFromError
  ]);

  const handleSearchInputChange = useCallback((e) => {
    isUserTypingRef.current = true;
    setSuppressPredictions(false);
    setSearchInput(e.target.value);
  }, []);

  useEffect(() => {
    if (!isUserTypingRef.current) return undefined;

    const query = searchInput.trim();

    if (query.length < AUTOCOMPLETE_MIN_CHARS) {
      clearSuggestions();

      return undefined;
    }

    const timer = setTimeout(() => {
      getSuggestions(query, countryPlacesScope.autocomplete);
    }, AUTOCOMPLETE_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchInput, countryPlacesScope, getSuggestions, clearSuggestions]);

  const handleSelectPrediction = useCallback(async (prediction) => {
    await performStoreLocatorSearch({
      placeId: prediction.place_id,
      label: prediction.description || '',
    });
  }, [performStoreLocatorSearch]);

  const handleSearchKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      performStoreLocatorSearch({query: searchInput});
    }
  }, [searchInput, performStoreLocatorSearch]);

  const handleUseCurrentLocation = useCallback(() => {
    performStoreLocatorSearch({useCurrentLocation: true});
  }, [performStoreLocatorSearch]);

  // Determine if we should show the location dropdown
  // Only hide if permission was explicitly denied by the user
  // For other errors (timeout, position_error), user can still try again
  const shouldShowLocationDropdown = locationPermission !== LOCATION_PERMISSION.DENIED &&
    locationPermission !== LOCATION_PERMISSION.UNAVAILABLE;

  // KL-1081 — predictions actually shown to the shopper (hidden while suppressed).
  const visiblePredictions = suppressPredictions ? [] : predictions;

  // After the user sets "My showroom", reset the store column scroll to 0 and align the top of
  // showrooms-grid with the bottom of the sticky header (uses --header-height from _app, KINDW-684).
  useLayoutEffect(() => {
    if (!shouldScrollNewMyShowroomIntoViewRef.current || !myShowroom?.id) {
      return;
    }
    shouldScrollNewMyShowroomIntoViewRef.current = false;

    const scrollPageToGridBelowHeader = () => {
      if (typeof window === 'undefined' || !showroomsGridRef.current) {
        return;
      }

      const rootStyle = getComputedStyle(document.documentElement);
      const headerHRaw = rootStyle.getPropertyValue('--header-height').trim();
      const headerHeight = headerHRaw ? parseFloat(headerHRaw) : 0;
      const gridEl = showroomsGridRef.current;
      const rect = gridEl.getBoundingClientRect();
      const y = rect.top + (window.pageYOffset || document.documentElement.scrollTop || 0) - headerHeight;

      window.scrollTo({behavior: 'smooth', top: Math.max(0, y)});
    };

    // Wait two frames so the "My showroom" block is in the layout, then smooth-scroll the list column
    // (native smooth behavior; one call avoids resetting the scroll animation).
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const col = storeListingColumnRef.current;

        if (col) {
          col.scrollTo({behavior: 'smooth', top: 0});
        }

        requestAnimationFrame(() => {
          scrollPageToGridBelowHeader();
        });
      });
    });
  }, [myShowroom?.id]);

  // Handle setting a store as my showroom
  const handleSetMyShowroom = useCallback((store) => {
    // Get coordinates - support both SCAPI format (latitude/longitude) and mock format (coordinates.lat/lng)
    const lat = store.latitude || store.coordinates?.lat || null;
    const lng = store.longitude || store.coordinates?.lng || null;

    // Save full store data to cookie for persistent display
    // This ensures My Showroom can be displayed even when not in search results
    const showroomData = {
      id: store.id,
      name: store.name,
      slug: getStoreSlug(store),
      lat,
      lng,
      // Include display data for permanent visibility
      address1: store.address1 || store.address?.address1,
      city: store.city || store.address?.city,
      stateCode: store.stateCode || store.address?.state,
      postalCode: store.postalCode || store.address?.postalCode,
      phone: store.phone,
      email: store.email,
      c_slug: store.c_slug,
    };

    // Save to cookie using shared helper
    setMyShowroomToCookie(siteId, showroomData);
    shouldScrollNewMyShowroomIntoViewRef.current = true;
    setMyShowroom(showroomData);
  }, [siteId]);

  // Check if a store is the user's selected showroom
  const isStoreMyShowroom = useCallback((store) => {
    return myShowroom && myShowroom.id === store.id;
  }, [myShowroom]);

  // Get the My Showroom store object - prioritize: search results > cookie data
  // Cookie data contains all display info (address, phone, email) saved when user set it
  // This avoids extra API calls while ensuring My Showroom is always displayed
  const myShowroomStore = useMemo(() => {
    if (!myShowroom?.id) return null;

    // Priority 1: If in search results, use that (has fresh distance data)
    const storeInResults = stores.find((store) => store.id === myShowroom.id);

    if (storeInResults) {
      return storeInResults;
    }

    // Priority 2: Use cookie data (has all display info saved when user set it)
    if (myShowroom?.name) {
      return myShowroom;
    }

    return null;
  }, [stores, myShowroom]);

  // KL-1131: when search results include My Showroom, refresh cookie slug from SFCC c_slug
  useEffect(() => {
    if (!siteId || !myShowroom?.id) return;

    const storeInResults = stores.find((store) => store.id === myShowroom.id);

    if (!storeInResults?.c_slug) return;

    const resolvedSlug = getStoreSlug(storeInResults);

    if (!resolvedSlug || resolvedSlug === myShowroom.slug) return;

    const updatedShowroom = {
      ...myShowroom,
      slug: resolvedSlug,
      c_slug: storeInResults.c_slug,
    };

    setMyShowroomToCookie(siteId, updatedShowroom);
    setMyShowroom(updatedShowroom);
  }, [stores, myShowroom, siteId]);

  const countryTabs = useMemo(
    () => buildShowroomCountryTabs({
      currentSiteId: siteId,
      cmsCountryCodes,
      adminByCountry,
      currentTotal: currentSiteStoreTotal,
      adminHydrated,
    }),
    [
      adminByCountry,
      adminHydrated,
      cmsCountryCodes,
      currentSiteStoreTotal,
      siteId
    ]
  );

  useEffect(() => {
    if (!adminHydrated || isViewingCurrentSite) return;
    if (!adminByCountry?.[selectedCountryCode]) {
      setSelectedCountryCode(currentCountryCode);
      resetAdminSearch();
      replaceShowroomCountryInUrl(currentCountryCode);
    }
  }, [
    adminByCountry,
    adminHydrated,
    currentCountryCode,
    isViewingCurrentSite,
    resetAdminSearch,
    selectedCountryCode
  ]);

  useEffect(() => {
    setSelectedCountryCode(currentCountryCode);
    resetAdminSearch();
    countryUrlHydratedRef.current = false;
  }, [currentCountryCode, resetAdminSearch]);

  useEffect(() => {
    if (countryUrlHydratedRef.current || !cmsCountryCodes.length) return;

    const fromUrl = resolveShowroomCountryFromSearchParams(
      new URLSearchParams(location.search),
      cmsCountryCodes
    );

    if (fromUrl) setSelectedCountryCode(fromUrl);
    countryUrlHydratedRef.current = true;
  }, [cmsCountryCodes, location.search]);

  const handleCountrySelect = useCallback((code) => {
    setSelectedCountryCode(code);
    replaceShowroomCountryInUrl(code);
    resetAdminSearch();
    setSearchInput('');
    isUserTypingRef.current = false;
    clearSuggestions();
    setSuppressPredictions(true);
  }, [clearSuggestions, resetAdminSearch]);

  const displaySearchError = isViewingCurrentSite ? searchError : adminSearchError;

  const displayedStores = useMemo(() => {
    if (isViewingCurrentSite) return stores || [];

    if (Array.isArray(adminSearchStores)) return adminSearchStores;

    return adminByCountry?.[selectedCountryCode]?.data || [];
  }, [
    adminByCountry,
    adminSearchStores,
    isViewingCurrentSite,
    selectedCountryCode,
    stores
  ]);

  const displayedMarkers = useMemo(() => {
    if (isViewingCurrentSite) return markers;

    return createStoreMarkers(displayedStores, markerIconSize);
  }, [displayedStores, isViewingCurrentSite, markerIconSize, markers]);

  const adminMapBounds = useMemo(() => {
    if (!Array.isArray(adminSearchStores) || adminSearchStores.length === 0) return null;
    if (adminSearchOrigin?.lat == null || adminSearchOrigin?.lng == null) return null;

    return computeStoreClusterFocusBounds(adminSearchStores, {
      distanceUnit: countryPlacesScope.distanceUnit,
      origin: adminSearchOrigin,
    });
  }, [adminSearchOrigin, adminSearchStores, countryPlacesScope.distanceUnit]);

  const mapFocusBounds = isViewingCurrentSite ? mapBounds : adminMapBounds;

  const isListLoading = isViewingCurrentSite ?
    (isInitialLoad || isSearchLoading) :
    (isAdminStoresLoading || isAdminSearchLoading);

  // Get stores excluding my showroom for the result list (avoid duplicate display)
  const otherStores = useMemo(() => {
    if (!isViewingCurrentSite) return displayedStores;
    if (!myShowroom?.id) return displayedStores;

    return displayedStores.filter((store) => store.id !== myShowroom.id);
  }, [displayedStores, isViewingCurrentSite, myShowroom?.id]);

  const handleMarkerClick = useCallback((markerInfo) => {
    const store = displayedStores.find((item) => item.id === markerInfo?.storeId);

    if (!store) return;

    navigate(buildUrl(`/showroom/${getStoreSlug(store)}`));
  }, [buildUrl, displayedStores, navigate]);

  if (!apiKey) {
    return (
      <Box {...styles.errorContainer}>
        <Alert status="error">
          <AlertIcon />
          <Text>
            <FormattedMessage
              defaultMessage="Google Maps API key is not configured. Please check your environment variables."
              id="store_locator.error.no_api_key"
            />
          </Text>
        </Alert>
      </Box>
    );
  }

  return (
    <Box className="showrooms-component" {...styles.container}>
      <Seo
        description={showroomSeoDescription}
        jsonLdGraph={jsonLdGraph}
        title={showroomSeoTitle}
      />

      {/* Showroom List Header */}
      <ShowroomListHeader
        countries={countryTabs}
        heroData={globalConfig?.showroomHero}
        isLoading={isLoadingGlobalConfig}
        selectedCountryCode={selectedCountryCode}
        onCountrySelect={handleCountrySelect}
      />

      {/* Mobile Map - Show below on smaller screens */}
      <Box className="showrooms-mobile-map" {...styles.mobileMap}>
        <GoogleMap
          apiKey={apiKey}
          center={mapCenter || {lat: defaultLocation.lat, lng: defaultLocation.lng}}
          fitBoundsToMarkers={true}
          focusBounds={mapFocusBounds}
          height="258px"
          markers={displayedMarkers}
          zoom={defaultZoom}
          error={intl.formatMessage({
            defaultMessage: 'Failed to load map. Please check your internet connection.',
            id: 'store_locator.map.error',
          })}
          loading={intl.formatMessage({
            defaultMessage: 'Loading map...',
            id: 'store_locator.map.loading',
          })}
          onMarkerClick={handleMarkerClick}
        />
      </Box>

      {/* Main Content - Grid Layout */}
      <Grid ref={showroomsGridRef} className="showrooms-grid" {...styles.grid}>
        {/* Left Column: Search + Store List (1/3) */}
        <GridItem
          ref={storeListingColumnRef}
          className="showrooms-column showrooms-left-column"
          {...styles.leftColumn}
        >
          <VStack {...styles.contentStack}>
            <Box className="heading-container" {...styles.headingContainer}>
              {/* Page Title */}
              <Heading as="h1" {...styles.heading}>
                <FormattedMessage
                  defaultMessage="Find a showroom"
                  id="store_locator.heading.title"
                />
              </Heading>

              {/* Search Section */}
              <Box ref={searchDropdownRef} {...styles.searchContainer}>
                <InputGroup>
                  <Input
                    {...styles.searchInput}
                    id="store-locator-search-input"
                    size="lg"
                    value={searchInput}
                    aria-label={intl.formatMessage({
                      defaultMessage: 'Search by postcode or suburb',
                      id: 'store_locator.search.label',
                    })}
                    placeholder={intl.formatMessage({
                      defaultMessage: 'Search by postcode or suburb',
                      id: 'store_locator.search.placeholder',
                    })}
                    onChange={handleSearchInputChange}
                    onFocus={() => setIsSearchFocused(true)}
                    onKeyDown={handleSearchKeyDown}
                  />
                  <InputRightElement h="full" pr={2}>
                    {(isViewingCurrentSite ? isSearchLoading : isAdminSearchLoading) ? (
                      <Spinner color="gray.400" size="sm" />
                    ) : (
                      <SearchIcon
                        _hover={{color: 'black'}}
                        boxSize="24px"
                        color="brandColors.greyscale"
                        cursor="pointer"
                        aria-label={intl.formatMessage({
                          defaultMessage: 'Search',
                          id: 'store_locator.search.button_label',
                        })}
                        onClick={() => performStoreLocatorSearch({query: searchInput})}
                      />
                    )}
                  </InputRightElement>
                </InputGroup>

                {/* KL-991 — Google Places suggestions (floating dropdown, overlays the list). */}
                <AddressAutocompleteUI
                  predictions={visiblePredictions}
                  onSelectPrediction={handleSelectPrediction}
                />

                {/* Dropdown - Use my current location (only show if permission not denied and
                    no Places suggestions are currently showing, to avoid two overlapping menus) */}
                {isSearchFocused && shouldShowLocationDropdown && visiblePredictions.length === 0 && (
                  <Box {...styles.searchDropdown}>
                    <Box
                      {...styles.dropdownItem}
                      onClick={handleUseCurrentLocation}
                    >
                      <HStack spacing={3}>
                        {isGeolocationLoading ? (
                          <Spinner color="gray.500" size="sm" />
                        ) : (
                          <LocationIcon boxSize={5} color="gray.500" />
                        )}
                        <Text {...styles.dropdownItemText}>
                          <FormattedMessage
                            defaultMessage="Use my current location"
                            id="store_locator.search.use_current_location"
                          />
                        </Text>
                      </HStack>
                    </Box>
                  </Box>
                )}
              </Box>

              {/* Location Permission Error - Show when user denied location access */}
              {locationPermission === LOCATION_PERMISSION.DENIED && (
                <Alert borderRadius="md" status="warning">
                  <AlertIcon />
                  <Text fontSize="sm">
                    <FormattedMessage
                      defaultMessage="Location access denied. Please enable location services in your browser settings to use this feature."
                      id="store_locator.error.location_denied"
                    />
                  </Text>
                </Alert>
              )}

              {/* Location Unavailable Error - Show when geolocation is not supported */}
              {locationPermission === LOCATION_PERMISSION.UNAVAILABLE && (
                <Alert borderRadius="md" status="warning">
                  <AlertIcon />
                  <Text fontSize="sm">
                    <FormattedMessage
                      defaultMessage="Location services are not available on your device."
                      id="store_locator.error.location_unavailable"
                    />
                  </Text>
                </Alert>
              )}

              {/* Location Timeout Error - Show when location request took too long */}
              {locationPermission === LOCATION_PERMISSION.TIMEOUT && (
                <Alert borderRadius="md" status="warning">
                  <AlertIcon />
                  <Text fontSize="sm">
                    <FormattedMessage
                      defaultMessage="Location request timed out. Please try again or search by suburb/postcode."
                      id="store_locator.error.location_timeout"
                    />
                  </Text>
                </Alert>
              )}

              {/* Position Error - Show when position could not be determined */}
              {locationPermission === LOCATION_PERMISSION.POSITION_ERROR && (
                <Alert borderRadius="md" status="warning">
                  <AlertIcon />
                  <Text fontSize="sm">
                    <FormattedMessage
                      defaultMessage="Could not determine your location. Please try again or search by suburb/postcode."
                      id="store_locator.error.position_error"
                    />
                  </Text>
                </Alert>
              )}

              {/* Search Error Messages */}
              {displaySearchError && (
                <Alert borderRadius="md" status="error">
                  <AlertIcon />
                  <Text fontSize="sm">
                    {displaySearchError.startsWith('NO_RESULTS:') ? (
                      <FormattedMessage
                        defaultMessage="No results for ''{searchTerm}''"
                        id="store_locator.error.no_results"
                        values={{searchTerm: displaySearchError.replace('NO_RESULTS:', '')}}
                      />
                    ) : (
                      <FormattedMessage
                        defaultMessage="Something went wrong with the search. Please try again."
                        id="store_locator.error.search_failed"
                      />
                    )}
                  </Text>
                </Alert>
              )}

              {/* Address search returned 0: show message and nearest 10 stores below */}
              {isViewingCurrentSite && !isInitialLoad && !isSearchLoading && isFallbackResults && stores.length > 0 && (
                <Alert borderRadius="md" status="warning">
                  <AlertIcon />
                  <Text fontSize="sm">
                    {(searchQuery || searchInput) ? (
                      <FormattedMessage
                        defaultMessage="no result for ''{searchTerm}''. Showing nearest showrooms instead."
                        id="store_locator.warning.fallback_results"
                        values={{searchTerm: searchQuery || searchInput}}
                      />
                    ) : (
                      <FormattedMessage
                        defaultMessage="No result for your current location. Showing nearest showrooms instead."
                        id="store_locator.warning.fallback_results_location"
                      />
                    )}
                  </Text>
                </Alert>
              )}

            </Box>

            {/* Store List Section */}
            <VStack className="showrooms-store-list" {...styles.storeList}>
              {/* My Showroom — current tab only */}
              {isViewingCurrentSite && !isListLoading && myShowroomStore && (
                <Box className="store-tile-active" {...styles.myShowroomTileContainer}>
                  <StoreTile
                    isMyShowroom={true}
                    store={myShowroomStore}
                    onSetMyShowroom={handleSetMyShowroom}
                  />
                </Box>
              )}

              {isListLoading && (
                <Flex {...styles.loadingContainer}>
                  <VStack {...styles.loadingStack}>
                    <Spinner color="gray.500" size="lg" />
                    <Text {...styles.loadingText}>
                      <FormattedMessage
                        defaultMessage="Loading showrooms..."
                        id="store_locator.loading.showrooms"
                      />
                    </Text>
                  </VStack>
                </Flex>
              )}

              {!isListLoading && otherStores.length === 0 && (
                <Alert {...styles.emptyState} status="warning">
                  <AlertIcon />
                  <Text fontSize="sm">
                    {isViewingCurrentSite && myShowroomStore ? (
                      <FormattedMessage
                        defaultMessage="No other showrooms found in this area."
                        id="store_locator.empty.no_other_showrooms"
                      />
                    ) : (
                      <FormattedMessage
                        defaultMessage="No showrooms found. Try searching for a different location."
                        id="store_locator.empty.no_showrooms"
                      />
                    )}
                  </Text>
                </Alert>
              )}

              {!isListLoading && otherStores.map((store) => (
                <StoreTile
                  key={store.id}
                  isMyShowroom={isViewingCurrentSite && isStoreMyShowroom(store)}
                  showSetMyShowroom={isViewingCurrentSite}
                  store={store}
                  onSetMyShowroom={handleSetMyShowroom}
                />
              ))}
            </VStack>
          </VStack>
        </GridItem>

        {/* Right Column: Map (2/3) - Same height as left panel */}
        <GridItem className="showrooms-column showrooms-right-column" {...styles.rightColumn}>
          <GoogleMap
            apiKey={apiKey}
            center={mapCenter || {lat: defaultLocation.lat, lng: defaultLocation.lng}}
            fitBoundsToMarkers={true}
            focusBounds={mapFocusBounds}
            height="100%"
            markers={displayedMarkers}
            zoom={defaultZoom}
            error={intl.formatMessage({
              defaultMessage: 'Failed to load map. Please check your internet connection.',
              id: 'store_locator.map.error',
            })}
            loading={intl.formatMessage({
              defaultMessage: 'Loading map...',
              id: 'store_locator.map.loading',
            })}
            onMarkerClick={handleMarkerClick}
          />
        </GridItem>
      </Grid>
    </Box>
  );
};

StoreLocatorPage.displayName = 'StoreLocatorPage';

export default StoreLocatorPage;
